import {
  Box,
  Tab,
  Tabs,
  styled,
  Button,
  Divider,
  Skeleton,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import React, { memo, useCallback, useMemo } from 'react';
import theme from '../../../theme';
import {
  DownloadSimpleIcon,
  CopyIcon,
  CheckIcon,
  ArrowClockwiseIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import CallTranscription, { CallDetailTabsProps } from './CallTranscription';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';
import { getAgentSummaryApi } from '../../../utils/apis';
import { useQuery } from '@tanstack/react-query';
import CallSummary from './CallSummary';
import useCopyToClipboard from '../../../hooks/useCopyToClipboard';
import ServerLogs from '../../../pages/ServerLogs';
import { useLocation } from 'react-router-dom';
import useServerLogsData from '../../../hooks/useServerLogsData';

const useQueryParams = () => {
  return new URLSearchParams(useLocation().search);
};

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    letterSpacing: '-0.16px',
    padding: '8px 16px',
    color: '#6B6F80',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: '#E7F0FF',
      borderRadius: '6px',
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return value === index ? <>{children}</> : null;
};

const CallDetailTabs: React.FC<CallDetailTabsProps> = ({
  messages = [],
  id,
  agentName,
  callType,
  to,
  from,
  config,
  isLoading,
  audio,
  cost,
  billedFor,
  sid,
  selectedAssistantId,
}) => {
  const queryParams = useQueryParams();
  const callSid = queryParams.get('callSid');
  const agentId = queryParams.get('agentId');

  const [selectedTab, setSelectedTab] = React.useState(0);
  const [transcriptCopied, setTranscriptCopied] = React.useState(false);
  const [summaryCopied, setSummaryCopied] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const { showAlert, alert, setAlert } = useAlert();
  const { copy } = useCopyToClipboard();

  const { fetchLogData, isLoading: isLoadingServerLogs } = useServerLogsData(
    callSid || '',
    agentId || ''
  );

  const { data: agentSummaryData } = useQuery({
    queryKey: ['get-agent-summary', id],
    queryFn: () => getAgentSummaryApi(id || ''),
    enabled: !!id && selectedTab === 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
  });

  const handleDownload = useCallback(async () => {
    if (!audio) {
      showAlert('No audio file available for download', 'error');
      return;
    }

    try {
      // Show loading state
      const fileName = audio.split('/').pop() || 'recording.mp3';

      // Fetch the audio file as a blob
      const response = await fetch(audio);
      if (!response.ok) {
        throw new Error(`Failed to fetch audio file: ${response.status}`);
      }

      const blob = await response.blob();

      // Create a blob URL with proper MIME type
      const audioBlob = new Blob([blob], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(audioBlob);

      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName.endsWith('.mp3') ? fileName : `${fileName}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);

      showAlert('Download started successfully', 'success');
    } catch (error: any) {
      showAlert(error?.message || 'Failed to download audio file', 'error');
    }
  }, [audio, showAlert]);

  const handleCopyTranscript = useCallback(() => {
    if (!messages || messages.length === 0) {
      showAlert('No transcript available to copy', 'error');
      return;
    }

    try {
      const transcript = messages
        .filter(message => message.message.trim() !== '')
        .map(message => {
          const role = message.role === 'user' ? 'Client' : 'Assistant';
          return `${role}: ${message.message}`;
        })
        .join('\n\n');

      copy(transcript);
      setTranscriptCopied(true);
      setTimeout(() => {
        setTranscriptCopied(false);
      }, 3000);
    } catch (error: any) {
      showAlert(error?.message || 'Failed to copy transcript', 'error');
    }
  }, [messages, copy, showAlert]);

  const handleCopySummary = useCallback(() => {
    const summaryText = agentSummaryData?.data?.data?.summary;

    if (!summaryText) {
      showAlert('No summary available to copy', 'error');
      return;
    }

    try {
      copy(summaryText);
      setSummaryCopied(true);
      setTimeout(() => {
        setSummaryCopied(false);
      }, 3000);
    } catch (error: any) {
      showAlert(error?.message || 'Failed to copy summary', 'error');
    }
  }, [agentSummaryData, copy, showAlert]);

  const handleCopyClick = useCallback(() => {
    if (selectedTab === 0) {
      handleCopyTranscript();
    } else if (selectedTab === 1) {
      handleCopySummary();
    }
  }, [selectedTab, handleCopyTranscript, handleCopySummary]);

  const handleRefresh = useCallback(() => {
    if (callSid && agentId) {
      fetchLogData();
    }
  }, [callSid, agentId, fetchLogData]);

  const tabItems = useMemo(
    () => [
      {
        label: 'Transcript',
        desc: (
          <CallTranscription
            messages={messages}
            audio={audio}
            cost={cost}
            billedFor={billedFor}
            id={id}
            to={to}
            from={from}
            config={config}
            isLoading={isLoading}
            callType={callType}
            agentName={agentName}
            sid={sid}
          />
        ),
      },
      {
        label: 'Summary',
        desc: (
          <CallSummary selectedAssistantId={selectedAssistantId} chatId={id} />
        ),
      },
      {
        label: 'Server Logs',
        desc: (
          <ServerLogs
            callSid={callSid || ''}
            agentId={agentId || ''}
            searchTerm={searchTerm}
          />
        ),
      },
    ],
    [
      messages,
      audio,
      cost,
      billedFor,
      id,
      to,
      from,
      config,
      isLoading,
      callType,
      agentName,
      sid,
      selectedAssistantId,
      callSid,
      agentId,
      searchTerm,
    ]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    },
    []
  );

  const commonBoxStyles = {
    display: 'flex',
    borderRadius: '8px',
    border: '1px solid #DCE0E7',
    backgroundColor: '#FFF',
    height: '100%',
  };

  const commonContentStyles = {
    width: '100%',
  };

  if (isLoading) {
    return (
      <Box sx={commonBoxStyles}>
        <Box sx={{ ...commonContentStyles, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[...Array(3)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width={100}
                  height={40}
                  sx={{ borderRadius: '6px' }}
                />
              ))}
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
              <Skeleton
                variant="rectangular"
                width={100}
                height={36}
                sx={{ borderRadius: '6px' }}
              />
            </Box>
          </Box>
          <Divider />
          <Box sx={{ mt: 2, p: '0px 24px 24px' }}>{tabItems[0].desc}</Box>
        </Box>
      </Box>
    );
  }

  const getCopyButtonProps = () => {
    if (selectedTab === 0) {
      return {
        text: transcriptCopied ? 'Copied!' : 'Copy Transcript',
        icon: transcriptCopied ? (
          <CheckIcon size={18} />
        ) : (
          <CopyIcon size={18} />
        ),
        disabled: !messages || messages.length === 0,
      };
    } else if (selectedTab === 1) {
      return {
        text: summaryCopied ? 'Copied!' : 'Copy Short Summary',
        icon: summaryCopied ? <CheckIcon size={18} /> : <CopyIcon size={18} />,
        disabled: !agentSummaryData?.data?.data?.summary,
      };
    }
    return {
      text: 'Copy',
      icon: <CopyIcon size={18} />,
      disabled: true,
    };
  };

  const copyButtonProps = getCopyButtonProps();

  return (
    <Box sx={commonBoxStyles}>
      <Box sx={commonContentStyles}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
          }}
        >
          <StyledTabs value={selectedTab} onChange={handleTabChange}>
            {tabItems.map((tab, i) => (
              <Tab key={i} label={tab.label} />
            ))}
          </StyledTabs>

          {selectedTab === 0 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                sx={{
                  display: 'flex',
                  padding: '8px 16px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '6px',
                  border: '1px solid primary.main',
                  textTransform: 'none',
                }}
                startIcon={copyButtonProps.icon}
                onClick={handleCopyClick}
                disabled={copyButtonProps.disabled}
              >
                {copyButtonProps.text}
              </Button>
              <Button
                sx={{
                  display: 'flex',
                  padding: '8px 16px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '6px',
                  border: '1px solid primary.main',
                  textTransform: 'none',
                }}
                startIcon={<DownloadSimpleIcon size={18} />}
                onClick={handleDownload}
                disabled={!audio}
              >
                Download Recording
              </Button>
            </Box>
          )}
          {selectedTab === 1 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                sx={{
                  display: 'flex',
                  padding: '8px 16px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '6px',
                  border: '1px solid primary.main',
                  textTransform: 'none',
                }}
                startIcon={copyButtonProps.icon}
                onClick={handleCopyClick}
                disabled={copyButtonProps.disabled}
              >
                {copyButtonProps.text}
              </Button>
            </Box>
          )}
          {selectedTab === 2 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Search Bar */}
              <TextField
                placeholder="Search logs..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  minWidth: '350px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#eeeeee',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <MagnifyingGlassIcon size={20} color="#666" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Tooltip title="Refresh logs">
                <IconButton
                  onClick={handleRefresh}
                  disabled={isLoadingServerLogs}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                    '&:disabled': {
                      color: 'text.disabled',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      ...(isLoadingServerLogs && {
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          from: { transform: 'rotate(0deg)' },
                          to: { transform: 'rotate(360deg)' },
                        },
                      }),
                    }}
                  >
                    <ArrowClockwiseIcon size={20} />
                  </Box>
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Divider />
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            height: 'calc(100vh - 150px)',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {tabItems.map((tab, index) => (
            <TabPanel key={index} value={selectedTab} index={index}>
              {tab.desc}
            </TabPanel>
          ))}
        </Box>
      </Box>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default memo(CallDetailTabs);
